import React, { useEffect } from 'react';
import {
  AnalyticsContainer,
  ChartContainer,
  ChartContent,
  ChartHeader,
  Content,
  EmptyState,
  Line,
  PageContainer,
  SpinnerContainer,
  StatisticContainer,
  TitleRightSide,
  TitleRow,
  TotalContainer,
} from './HomePage.styles';
import { Tag, TagText } from '../../components/ReferralInfo/ReferralInfo.styles';
import { Text18Regular, Text20Bold, Text24Bold } from '../../components/utils/Text/Text.styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDateRange,
  selectEarningType,
  selectMonthlyInfluencerEarnings,
  selectUser,
  selectUserActionsPending,
  setEarningType,
} from '../../store/slices/user/slice';
import {
  AnalyticsCard,
  DateRangePickerComponent,
  HomeBarChart,
  InfluencerEarningsByMonth,
  Select,
  Spinner,
  StatisticCard,
} from '../../components';
import { ReactComponent as WalletIcon } from '../../assets/icons/wallet.svg';
import { ReactComponent as SafeIcon } from '../../assets/icons/safe.svg';
import { ReactComponent as CreditCardIcon } from '../../assets/icons/credit-card-down.svg';
import { ReactComponent as ImageIcon } from '../../assets/icons/image.svg';
import { ReactComponent as VideoRecorderIcon } from '../../assets/icons/video-recorder.svg';
import { ReactComponent as SaleIcon } from '../../assets/icons/sale.svg';
import { ReactComponent as FilmIcon } from '../../assets/icons/film.svg';
import { ReactComponent as StarIcon } from '../../assets/icons/star.svg';
import { EARNINGS_TYPE } from '../../utils/constants';
import { fetchUserEarnings, fetchUserMonthlyEarnings } from '../../store/slices/user/asyncThunks';
import { EarningContainer, NetText } from '../../components/home/AnalyticsCard/AnalyticsCard.styles';
import { formatPrice } from '../../utils/util';

const HomePage = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const range = useSelector(selectDateRange);
  const earningType = useSelector(selectEarningType);
  const { fetchUserEarnings: fetchUserEarningsPending, fetchUserMonthlyEarnings: fetchUserMonthlyEarningsPending } =
    useSelector(selectUserActionsPending);
  const monthlyInfluencerEarnings = useSelector(selectMonthlyInfluencerEarnings);
  const { earnings, daily, overview } = user.analytics;
  const hasEarningsData = Boolean(overview && Object.keys(overview).length);

  useEffect(() => {
    dispatch(fetchUserEarnings({ startDate: range[0], endDate: range[1], earningType: earningType.value }));
  }, [range, dispatch, earningType]);

  useEffect(() => {
    dispatch(fetchUserMonthlyEarnings());
  }, [dispatch]);

  return (
    <PageContainer>
      <TitleRow>
        <Text24Bold>{`Welcome, ${user.name}!`}</Text24Bold>
        {user.deletedAt && (
          <Tag>
            <TagText>Deleted</TagText>
          </Tag>
        )}
      </TitleRow>
      <Content>
        <StatisticContainer>
          <StatisticCard title="Available Balance" icon={<CreditCardIcon />} value={earnings.available} hideGross />
          <StatisticCard
            title="Pending Balance"
            icon={<WalletIcon />}
            value={earnings.balance - earnings.available}
            hideGross
          />
          <StatisticCard
            title="Lifetime Proceeds"
            icon={<SafeIcon />}
            value={earnings.lifetime.net}
            gross={earnings.lifetime.gross}
          />
        </StatisticContainer>
        <ChartContainer>
          <ChartHeader>
            <Text20Bold>Overview</Text20Bold>
            <TitleRightSide>
              <DateRangePickerComponent />
              <Select
                options={[
                  { value: EARNINGS_TYPE.GROSS, label: 'Gross' },
                  { value: EARNINGS_TYPE.NET, label: 'Net' },
                ]}
                handleChange={value => dispatch(setEarningType(value))}
                selectedOption={earningType}
                hideError
              />
            </TitleRightSide>
          </ChartHeader>
          {!daily || !overview || fetchUserEarningsPending ? (
            <SpinnerContainer>
              <Spinner width={150} height={150} />
            </SpinnerContainer>
          ) : !hasEarningsData ? (
            <EmptyState>No earnings for the selected period.</EmptyState>
          ) : (
            <ChartContent>
              <HomeBarChart dailyAnalytics={daily} />
              <Line />
              <AnalyticsContainer>
                <AnalyticsCard icon={<ImageIcon />} title="Photo Sales" earnings={overview.photo.earnings} />
                <AnalyticsCard
                  icon={<VideoRecorderIcon color="#97C9F1" />}
                  title="Video Sales"
                  earnings={overview.video.earnings}
                />
                <AnalyticsCard icon={<FilmIcon />} title="Bundle Sales" earnings={overview.bundle.earnings} />
                <AnalyticsCard
                  icon={<StarIcon />}
                  title="Subscription Sales"
                  earnings={overview.subscription.earnings}
                />
                <AnalyticsCard icon={<SaleIcon />} title="Other Sales" earnings={overview.other.earnings} />
                <TotalContainer>
                  <EarningContainer />
                  <EarningContainer>
                    <NetText>Total:</NetText>
                    <Text18Regular>${formatPrice(overview.total.earnings)}</Text18Regular>
                  </EarningContainer>
                </TotalContainer>
              </AnalyticsContainer>
            </ChartContent>
          )}
        </ChartContainer>
        <InfluencerEarningsByMonth
          monthlyInfluencerEarnings={monthlyInfluencerEarnings}
          pending={fetchUserMonthlyEarningsPending}
        />
      </Content>
    </PageContainer>
  );
};

export default HomePage;
